import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Shield, Scales, Lightning } from "@phosphor-icons/react";
import { api } from "@/api";

const PRESET_ICON = {
  safe: Shield,
  balanced: Scales,
  aggressive: Lightning,
};
const PRESET_COLOR = {
  safe: "#00FF66",
  balanced: "#F5C518",
  aggressive: "#FF3B30",
};

export function FilterPresets() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["signal-presets"],
    queryFn: api.signalPresets,
    refetchInterval: 30_000,
  });

  const setPreset = useMutation({
    mutationFn: (p) => api.setSignalPreset(p),
    onSuccess: (d) => {
      toast.success(`Filter mode: ${d.preset?.label || d.active}`);
      qc.invalidateQueries({ queryKey: ["signal-presets"] });
      qc.invalidateQueries({ queryKey: ["scanner-status"] });
    },
    onError: () => toast.error("Failed to switch preset"),
  });

  const active = q.data?.active || "balanced";
  const presets = q.data?.presets || {};

  return (
    <div className="border border-white/10 bg-black" data-testid="filter-presets">
      <div className="px-4 py-2 border-b border-white/10 bg-[#0f0f0f] flex items-center gap-2">
        <Scales size={13} className="text-[#F5C518]" />
        <span className="font-display text-[11px] tracking-widest uppercase text-white/70">
          Signal Filter Mode
        </span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-white/40">
          affects live scanner
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 p-3">
        {["safe", "balanced", "aggressive"].map((k) => {
          const p = presets[k] || {};
          const Icon = PRESET_ICON[k];
          const isActive = active === k;
          const color = PRESET_COLOR[k];
          return (
            <button
              key={k}
              data-testid={`preset-${k}`}
              onClick={() => setPreset.mutate(k)}
              disabled={setPreset.isPending || isActive}
              className={`text-left border p-3 transition-colors ${
                isActive
                  ? "border-[color:var(--ac)] bg-[color:var(--ac)]/10"
                  : "border-white/10 hover:border-white/30"
              }`}
              style={{ "--ac": color }}
            >
              <div className="flex items-center gap-2">
                {Icon && <Icon size={14} weight={isActive ? "fill" : "regular"} style={{ color }} />}
                <span
                  className="font-display text-[13px] font-bold tracking-wide uppercase"
                  style={{ color: isActive ? color : "#fff" }}
                >
                  {p.label || k}
                </span>
                {isActive && (
                  <span className="ml-auto font-mono text-[9px] px-1.5 py-0.5 border" style={{ color, borderColor: color }}>
                    ACTIVE
                  </span>
                )}
              </div>
              <p className="mt-2 text-[11px] text-white/60 leading-relaxed">
                {p.description}
              </p>
              <div className="mt-2 flex flex-wrap gap-2 font-mono text-[9px] text-white/40">
                <span>
                  Score {p.min_score_offset >= 0 ? "+" : ""}{p.min_score_offset ?? 0}
                </span>
                <span>
                  Hits {p.min_hits_offset >= 0 ? "+" : ""}{p.min_hits_offset ?? 0}
                </span>
                <span>
                  ADX {p.min_adx_offset >= 0 ? "+" : ""}{p.min_adx_offset ?? 0}
                </span>
                <span>Lev ×{p.lev_scale ?? 1}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
